/**
 * ShopRegister.tsx — Customer portal registration (personal / retail only).
 * Trade accounts are created in CRM and invited via portal link.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import API_URL from '../../config/api';

const ShopRegister: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shop-auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId, name, email, phone, password, customerType: 'Retail' }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Registration failed'); return; }
      localStorage.setItem(`shopToken_${businessId}`, data.token);
      localStorage.setItem(`shopCustomer_${businessId}`, JSON.stringify(data.customer));
      navigate(`/shop/${businessId}`);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 440, marginBottom: 12 }}>
        <Link to={`/shop/${businessId}`} style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
          ← Back to shop
        </Link>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 440, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🛍️</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111827' }}>Create account</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280' }}>Track orders and checkout faster</p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#dc2626' }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div>
            <label style={labelStyle}>Full name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required autoComplete="name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Email address *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoComplete="email" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Phone (optional)</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+44 7700 000000" autoComplete="tel" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Password * <span style={{ color: '#9ca3af', fontWeight: 400 }}>(min 6 characters)</span></label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="new-password" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Confirm password *</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              style={{ ...inputStyle, borderColor: confirm && confirm !== password ? '#ef4444' : '#e5e7eb' }}
            />
            {confirm && confirm !== password && <p style={{ color: '#ef4444', fontSize: 12, margin: '4px 0 0' }}>Passwords do not match</p>}
          </div>

          <button type="submit" disabled={loading} style={{ padding: '13px 0', borderRadius: 9, border: 'none', background: loading ? '#93c5fd' : '#1e3a8a', color: '#fff', fontWeight: 800, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer', marginTop: 4 }}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: 20, padding: '12px 14px', background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe', fontSize: 12, color: '#1e40af', lineHeight: 1.5 }}>
          Personal accounts see standard retail prices. For wholesale / trade pricing, contact the supplier — they will send you an invite link.
        </div>

        <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6b7280' }}>
          Already have an account?{' '}
          <Link to={`/shop/${businessId}/login`} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb',
  fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fafafa',
};

export default ShopRegister;
